import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "../hooks/useTranslation";
import { FiSettings, FiArrowDown, FiInfo, FiX } from "react-icons/fi";
import { useWallet } from "../hooks/useWallet";
import { toast } from "react-hot-toast";
import { useRPCProvider } from "../hooks/useRPCProvider";
import { CONTRACT_ADDRESSES, CONTRACT_ABIS, API_BASE } from "../constants/index";
import { ethers } from "ethers";
import { AnimatePresence, motion } from "framer-motion";
import tokenIconUsdt from "../images/tokens-icon-usdt.png";

export default function Swap() {
    const { t } = useTranslation();
    const { account, isConnected, isCorrectNetwork } = useWallet();
    const { executeWithFailover, getWalletSigner } = useRPCProvider();

    const [fromAmount, setFromAmount] = useState("");
    const [toAmount, setToAmount] = useState("");
    const [isSwapping, setIsSwapping] = useState(false);
    const [isLoadingBalance, setIsLoadingBalance] = useState(false);
    const [usdtBalance, setUsdtBalance] = useState("0");

    // Add approve states
    const [isApproving, setIsApproving] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showSwapModal, setShowSwapModal] = useState(false);
    const [showNewSwapModal, setShowNewSwapModal] = useState(false);
    const [approvalStatus, setApprovalStatus] = useState(null);
    const [approvalError, setApprovalError] = useState(null);
    const [abnbUsdtBalance, setAbnbUsdtBalance] = useState("0");
    const [swapPair, setSwapPair] = useState("ABNB_TO_USDT"); // ABNB_TO_USDT or USDT_TO_GRAM
    const [usdtApiBalance, setUsdtApiBalance] = useState("0");
    const [isLoadingUsdtApiBalance, setIsLoadingUsdtApiBalance] = useState(false);
    const [gramBalance, setGramBalance] = useState("0");
    const [isLoadingGramBalance, setIsLoadingGramBalance] = useState(false);
    const [receivedBalance, setReceivedBalance] = useState("0");

    // Mock prices and balances for now - replace with actual contract calls
    const exchangeRate = 1; // 1 FATOKEN = 0.5 USDT

    const formatBonusAmount = useCallback((val) => {
        if (val == null) return "0.00";
        const raw = typeof val === "bigint" ? val.toString() : (val?.toString ? val.toString() : String(val));
        const parsed = parseFloat(raw);
        if (Number.isNaN(parsed)) return "0.00";
        const value = parsed / 1e18;
        // avoid round up
        const floored = Math.floor(value * 100) / 100;
        return floored.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }, []);

    const fetchBalances = useCallback(async () => {
        if (!account || !isConnected || !isCorrectNetwork) {
            setUsdtBalance("0");
            setAbnbUsdtBalance("0");
            return;
        }
        setIsLoadingBalance(true);
        try {
            const { usdtBalance, abnbUsdtBalance } = await executeWithFailover(async (provider) => {
                const USDTContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.USDT,
                    CONTRACT_ABIS.USDT,
                    provider
                );

                const swapContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.SWAP,
                    CONTRACT_ABIS.SWAP,
                    provider
                );

                const usdtBalanceResult = await USDTContract.balanceOf(account);
                const abnbUsdtBalanceResult = await swapContract.balanceOf(account);

                const userAbnbBalance = ethers.formatUnits(abnbUsdtBalanceResult, 18);
                const userUSDBalance = ethers.formatUnits(usdtBalanceResult, 18);

                const fixedUserAbnbBalance = userAbnbBalance.includes('.')
                    ? userAbnbBalance.slice(0, userAbnbBalance.indexOf('.') + 7)
                    : userAbnbBalance;

                const fixedUserUSDBalance = userUSDBalance.includes('.')
                    ? userUSDBalance.slice(0, userUSDBalance.indexOf('.') + 7)
                    : userUSDBalance;

                return { usdtBalance: fixedUserUSDBalance, abnbUsdtBalance: fixedUserAbnbBalance };
            });

            setUsdtBalance(usdtBalance);
            setAbnbUsdtBalance(abnbUsdtBalance);
        } catch (error) {
            if (error?.code === "CALL_EXCEPTION") {
                console.warn("USDT balance fetch skipped until provider/network is ready.");
            } else {
                console.error("Error fetching USDT balance:", error);
            }
            setUsdtBalance("0");
        } finally {
            setIsLoadingBalance(false);
        }
    }, [account, isConnected, isCorrectNetwork, executeWithFailover]);

    const fetchUsdtApiBalance = useCallback(async () => {
        if (!account || !isConnected || !isCorrectNetwork) {
            setUsdtApiBalance("0");
            return;
        }
        setIsLoadingUsdtApiBalance(true);
        try {
            const res = await fetch(`${API_BASE}/publicApi/getBalance`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ wallet_address: account }),
            });
            const result = await res.json().catch(() => ({}));
            if (result.success && result.balance !== undefined) {
                setUsdtApiBalance(result.balance.toString());
            } else {
                setUsdtApiBalance("0");
            }
        } catch (error) {
            console.warn("Error fetching USDT API balance:", error);
            setUsdtApiBalance("0");
        } finally {
            setIsLoadingUsdtApiBalance(false);
        }
    }, [account, isConnected, isCorrectNetwork]);

    const fetchGramBalance = useCallback(async () => {
        if (!account || !isConnected || !isCorrectNetwork) {
            setGramBalance("0");
            return;
        }
        setIsLoadingGramBalance(true);
        try {
            const res = await fetch(`${API_BASE}/publicApi/getBalance`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ wallet_address: account }),
            });
            const result = await res.json().catch(() => ({}));
            if (result.success && result.gram_balance !== undefined) {
                setGramBalance(result.gram_balance.toString());
            } else {
                setGramBalance("0");
            }
        } catch (error) {
            console.warn("Error fetching GRAM balance:", error);
            setGramBalance("0");
        } finally {
            setIsLoadingGramBalance(false);
        }
    }, [account, isConnected, isCorrectNetwork]);

    const fetchAllBalances = useCallback(async () => {
        await fetchBalances();
        await fetchUsdtApiBalance();
        await fetchGramBalance();
    }, [fetchBalances, fetchUsdtApiBalance, fetchGramBalance]);

    useEffect(() => {
        fetchAllBalances();
    }, [fetchAllBalances]);

    const handleFromAmountChange = useCallback((e) => {
        const value = e.target.value.replace(/[^0-9.]/g, "");
        if (value.split(".").length > 2) return;

        setFromAmount(value);

        let to_amount = "0";

        if (value) {
            to_amount = (parseFloat(value) * exchangeRate).toFixed(2);
            setToAmount(to_amount);
        } else {
            setToAmount("0");
        }

        const targetBalance = swapPair === "ABNB_TO_USDT" ? usdtBalance : gramBalance;
        const currentBalance = parseFloat(targetBalance || "0");
        const total = currentBalance + parseFloat(to_amount || "0");

        // Use the same flooring logic as formatBonusAmount
        const floored = Math.floor(total * 100) / 100;
        setReceivedBalance(floored.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

    }, [usdtBalance, gramBalance, swapPair, exchangeRate]);

    const handlePairChange = useCallback((pair) => {
        setSwapPair(pair);
        setFromAmount("");
        setToAmount("");
        setReceivedBalance("0");
    }, []);

    const handleCloseApprove = useCallback(() => {
        if (isApproving) return;
        setShowApproveModal(false);
        setApprovalStatus(null);
        setApprovalError(null);
    }, [isApproving]);

    const checkAllowance = useCallback(async () => {
        if (!account) {
            toast.error(t("validation.connectWalletFirst"));
            return;
        }

        if (!fromAmount || parseFloat(fromAmount) <= 0) {
            toast.error(t("swap.selectTokensAndAmount") || "Please enter an amount");
            return;
        }

        if (swapPair === "USDT_TO_GRAM") {
            setShowNewSwapModal(true);
            return;
        }

        try {
            const signer = await getWalletSigner();

            const usdtContract = new ethers.Contract(
                CONTRACT_ADDRESSES.USDT,
                CONTRACT_ABIS.USDT,
                signer
            );

            const currentAllowance = await usdtContract.allowance(
                account,
                CONTRACT_ADDRESSES.SWAP
            );

            const approvalAmount = fromAmount
                ? ethers.parseUnits(fromAmount.toString(), 18)
                : ethers.MaxUint256;

            if (Number(currentAllowance) < Number(approvalAmount)) {
                setApprovalStatus(null);
                setApprovalError(null);
                setShowApproveModal(true);
            } else {
                setShowNewSwapModal(true);
            }
        } catch (error) {
            console.error("Allowance check error:", error);
            toast.error(t("swap.allowanceCheckError") || "Allowance check failed");
            setApprovalStatus(null);
            setApprovalError(null);
        }
    }, [account, fromAmount, getWalletSigner, t, swapPair]);

    const approveSwap = useCallback(async () => {
        if (!account) {
            setApprovalError(t("validation.connectWalletFirst"));
            setApprovalStatus('error');
            return;
        }

        setIsApproving(true);
        setApprovalStatus('pending');
        setApprovalError(null);

        try {
            const signer = await getWalletSigner();

            const usdtContract = new ethers.Contract(
                CONTRACT_ADDRESSES.USDT,
                CONTRACT_ABIS.USDT,
                signer
            );

            const approvalAmount = fromAmount
                ? ethers.parseUnits(fromAmount.toString(), 18)
                : ethers.MaxUint256;

            const approveTx = await usdtContract.approve(CONTRACT_ADDRESSES.SWAP, approvalAmount);

            toast.loading(t("validation.waitingApproval"), { id: "approval-tx" });
            await approveTx.wait();
            toast.success(t("validation.approvalSuccessful"), { id: "approval-tx" });
            setApprovalStatus('success');

            setTimeout(async () => {
                setShowApproveModal(false);
                setApprovalStatus(null);
                setApprovalError(null);
                setShowNewSwapModal(true);
            }, 2000);
        } catch (error) {
            console.error("Approve stake error:", error);
            const errorMessage =
                error?.reason ||
                error?.shortMessage ||
                error?.message ||
                t("validation.approvalFailed");
            setApprovalError(errorMessage);
            setApprovalStatus("error");
            toast.error(errorMessage);
        } finally {
            setIsApproving(false);
        }
    }, [account, fromAmount, getWalletSigner, t]);

    const handleSwap = useCallback(async () => {
        if (!isConnected) {
            toast.error(t("swap.connectWalletFirst") || "Please connect your wallet first");
            return;
        }
        if (!fromAmount || parseFloat(fromAmount) <= 0) {
            toast.error(t("swap.selectTokensAndAmount") || "Please enter an amount");
            return;
        }

        const currentFromBalance = swapPair === "ABNB_TO_USDT" ? abnbUsdtBalance : usdtApiBalance;
        if (parseFloat(fromAmount) > parseFloat(currentFromBalance)) {
            toast.error(t("swap.insufficientBalanceError") || "Insufficient balance");
            return;
        }

        setIsSwapping(true);
        try {
            if (swapPair === "ABNB_TO_USDT") {
                const signer = await getWalletSigner();

                const SwapContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.SWAP,
                    CONTRACT_ABIS.SWAP,
                    signer
                );

                let swapTransaction = null;

                const swapAmount = ethers.parseUnits(fromAmount.toString(), 18);

                swapTransaction = await SwapContract.swapABnbUSDTToUSDT(swapAmount);

                // Wait for transaction confirmation
                toast.loading(t("common.waitingConfirmation"), { id: "swap-tx" });
                await swapTransaction.wait();

                toast.success(
                    <div>
                        <p>{t("swap.swapSuccessful")}</p>
                        <p className="text-xs mt-1">
                            TX:{" "}
                            <a
                                href={`https://bscscan.com/tx/${swapTransaction.hash}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-500 hover:text-blue-700 underline"
                            >
                                {swapTransaction.hash.slice(0, 10)}...
                                {swapTransaction.hash.slice(-8)}
                            </a>
                        </p>
                    </div>,
                    { id: "swap-tx" }
                );
            } else {
                toast.loading(t("swap.swapping") || "Swapping...", { id: "swap-tx" });

                const generateUniqueId = () => {
                    const now = Date.now().toString();
                    const rand = Math.floor(Math.random() * 1_000_000)
                        .toString()
                        .padStart(6, "0");
                    return now + rand;
                };

                const uniqueId = generateUniqueId();

                const res = await fetch(`${API_BASE}/publicApi/swapUsdtToGram`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        address: account,
                        amount: fromAmount.toString(),
                        uniqueId: uniqueId,
                    }),
                });

                const data = await res.json().catch(() => ({}));

                if (!res.ok || !data.success) {
                    throw new Error(data.message || data.error || t("swap.swapFailed") || "Swap failed");
                }

                toast.success(
                    <div>
                        <p>{t("swap.swapSuccessful") || "Swap successful!"}</p>
                        {/* {data.txHash && (
                            <p className="text-xs mt-1">
                                TX:{" "}
                                <a
                                    href={`https://bscscan.com/tx/${data.txHash}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-500 hover:text-blue-700 underline"
                                >
                                    {data.txHash.slice(0, 10)}...
                                    {data.txHash.slice(-8)}
                                </a>
                            </p>
                        )} */}
                    </div>,
                    { id: "swap-tx" }
                );
            }

            setShowNewSwapModal(false);

            setFromAmount("");
            setToAmount("");
            setReceivedBalance("0");

            // Wait a bit for blockchain state to update, then refresh balances
            setTimeout(() => {
                fetchAllBalances();
            }, 1000);
        } catch (error) {
            console.error("Error swapping:", error);
            toast.dismiss("swap-tx");

            // Extract error message and code
            const errorMessage = error?.message || error?.toString() || "";
            const errorCode = error?.code || error?.error?.code || "";

            // Check for insufficient balance
            if (errorMessage.includes("insufficient funds") ||
                errorMessage.includes("insufficient balance") ||
                errorMessage.includes("balance too low") ||
                errorMessage.toLowerCase().includes("insufficient") ||
                errorCode === "INSUFFICIENT_FUNDS") {
                toast.error(t("swap.insufficientBalanceError"));
            } else {
                toast.error(errorMessage || t("swap.swapError"));
            }
        } finally {
            setIsSwapping(false);
        }

    }, [account, fromAmount, getWalletSigner, t, fetchAllBalances, swapPair, abnbUsdtBalance, usdtBalance, isConnected]);

    return (

        <div className="relative overflow-x-hidden min-h-[calc(100vh-100px)] flex flex-col items-center pt-8 pb-24">

            <div className="max-w-[550px] w-full mx-auto px-4 mb-16">
                {/* Header */}
                <div className="flex items-center gap-4 mb-10 w-full pt-4">
                    <button onClick={() => window.history.back()} className="material-symbols-outlined text-white text-2xl">arrow_back</button>
                    <h2 className="text-2xl font-medium text-white">{t("swap.title")}</h2>
                </div>

                {/* Main Swap Card */}
                <div className="w-full bg-transparent rounded-[2rem] border border-[#2A1A42] p-6 mb-4 relative overflow-visible shadow-lg shadow-[#A855F7]/5">

                    {/* Pair Switcher removed: Swap page now supports only aBunBUsdt → USDT */}
                    <div className="flex gap-2 mb-6 p-1 bg-[#05010D] border border-[#2A1A42] rounded-full">
                        <button
                            type="button"
                            onClick={() => handlePairChange("ABNB_TO_USDT")}
                            className="flex-1 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-all duration-200 bg-[#5B47CD] text-white shadow-md shadow-[#5B47CD]/30"
                        >
                            {t("swap.abnbUsdtToUsdt") || "aBunBUsdt → USDT"}
                        </button>
                    </div>

                    {/* From Section */}
                    <div className="relative mb-6">
                        <div className="flex justify-between items-start">
                            <div className="flex-1 w-[calc(100%-80px)]">
                                <p className="text-[#938F99] text-base font-medium mb-3">{t("swap.from")}</p>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0.0"
                                    value={fromAmount}
                                    onChange={handleFromAmountChange}
                                    className="w-full bg-transparent text-white text-[42px] font-bold leading-tight placeholder-white/20 focus:outline-none focus:ring-0 truncate border-none p-0 shadow-none"
                                    style={{ boxShadow: 'none' }}
                                />
                                <p className="text-[#938F99] text-sm mt-4">{t("swap.balance")} : {swapPair === "ABNB_TO_USDT" ? abnbUsdtBalance : usdtApiBalance}</p>
                            </div>

                            {/* From Token Pill */}
                            {swapPair === "ABNB_TO_USDT" ? (
                                <div className="w-[60px] h-[130px] rounded-[30px] border border-[#2A1A42] flex flex-col items-center py-3 bg-[#05010D] absolute -right-2 top-0 z-10 shadow-[0_0_15px_rgba(0,0,0,0.5)] transition-all">
                                    <div className="size-9 rounded-full bg-[#E5D9F2] flex items-center justify-center text-[11px] font-bold text-[#5B47CD] shadow-inner mb-2 shrink-0">
                                        aB
                                    </div>
                                    <span className="text-white text-[11px] font-medium mb-auto">aBnBUsdt</span>
                                    <span className="material-symbols-outlined text-[#938F99] text-sm">keyboard_arrow_down</span>
                                </div>
                            ) : (
                                <div className="w-[60px] h-[130px] rounded-[30px] border border-[#2A1A42] flex flex-col items-center py-3 bg-[#05010D] absolute -right-2 top-0 z-10 shadow-[0_0_15px_rgba(0,0,0,0.5)] transition-all">
                                    <div className="size-9 rounded-full shrink-0 mb-2 border border-[#2A1A42] bg-[#22C55E] flex items-center justify-center overflow-hidden">
                                        <img src={tokenIconUsdt} alt="USDT" className="size-[85%] object-cover rounded-full" />
                                    </div>
                                    <span className="text-white text-[11px] font-medium mb-auto">USDT</span>
                                    <span className="material-symbols-outlined text-[#938F99] text-sm">keyboard_arrow_down</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="relative h-12 flex justify-end pr-[3px] z-20 mb-6">
                        <div className="size-[45px] rounded-full bg-[#5B47CD] flex items-center justify-center transition-transform active:scale-90 shadow-[0_0_15px_rgba(91,71,205,0.4)] absolute top-1/2 -translate-y-1/2">
                            <span className="material-symbols-outlined text-white text-xl">arrow_downward</span>
                        </div>
                    </div>

                    {/* To Section */}
                    <div className="relative">
                        <div className="flex justify-between items-start">
                            <div className="flex-1 w-[calc(100%-80px)]">
                                <p className="text-[#938F99] text-base font-medium mb-3">{t('swap.to')}</p>
                                <input
                                    type="text"
                                    value={toAmount}
                                    readOnly
                                    placeholder="0.0"
                                    className="w-full bg-transparent text-white text-[42px] font-bold leading-tight placeholder-white/20 focus:outline-none focus:ring-0 truncate border-none p-0 shadow-none"
                                    style={{ boxShadow: 'none' }}
                                />
                                <p className="text-[#938F99] text-sm mt-4">{t('swap.balance')} : {swapPair === "ABNB_TO_USDT" ? usdtBalance : gramBalance}</p>
                            </div>

                            {/* To Token Pill */}
                            {swapPair === "ABNB_TO_USDT" ? (
                                <div className="w-[60px] h-[130px] rounded-[30px] border border-[#2A1A42] flex flex-col items-center py-3 bg-[#05010D] absolute -right-2 bottom-0 z-10 shadow-[0_0_15px_rgba(0,0,0,0.5)] transition-all">
                                    <span className="material-symbols-outlined text-[#938F99] text-sm mb-auto mt-1">keyboard_arrow_up</span>
                                    <div className="size-9 rounded-full shrink-0 mb-1 border border-[#2A1A42] bg-[#22C55E] flex items-center justify-center overflow-hidden">
                                        <img src={tokenIconUsdt} alt="USDT" className="size-[85%] object-cover rounded-full" />
                                    </div>
                                    <span className="text-white text-[11px] font-medium">USDT</span>
                                </div>
                            ) : (
                                <div className="w-[60px] h-[130px] rounded-[30px] border border-[#2A1A42] flex flex-col items-center py-3 bg-[#05010D] absolute -right-2 bottom-0 z-10 shadow-[0_0_15px_rgba(0,0,0,0.5)] transition-all">
                                    <span className="material-symbols-outlined text-[#938F99] text-sm mb-auto mt-1">keyboard_arrow_up</span>
                                    <div className="size-9 rounded-full bg-[#FEF3C7] flex items-center justify-center text-[11px] font-bold text-[#D97706] shadow-inner mb-1 shrink-0">
                                        GR
                                    </div>
                                    <span className="text-white text-[11px] font-medium">GRAM</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Rate Info Card */}
                <div className="w-full bg-transparent rounded-3xl border border-[#2A1A42] p-5 mb-8 flex justify-between items-center shadow-lg shadow-[#A855F7]/5">
                    <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-white text-[20px] shrink-0">info</span>
                        <div>
                            <p className="text-white text-[15px] font-medium">{t("swap.rate")}</p>
                            <p className="text-[#938F99] text-[11px] mt-1 whitespace-nowrap">Feb 10,12.20PM UTC</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-white text-[15px] font-medium">
                            {swapPair === "ABNB_TO_USDT" ? "1 aBnBUsdt = 1 USDT" : "1 USDT = 1 GRAM"}
                        </p>
                    </div>
                </div>

                {/* Swap Button */}
                <button
                    onClick={checkAllowance}
                    disabled={isSwapping || !fromAmount || parseFloat(fromAmount) <= 0}
                    className="w-full h-[54px] rounded-full bg-[#5B47CD] text-white font-bold text-lg hover:bg-[#4C3BBA] active:scale-[0.98] transition-all duration-200 shadow-[0_0_20px_rgba(91,71,205,0.3)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                    {!isConnected ? (
                        t("swap.connectWalletFirst") || "Connect Wallet"
                    ) : isSwapping ? (
                        <>
                            <div className="size-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            {t("swap.swapping") || "Swapping..."}
                        </>
                    ) : (
                        t("swap.swap") || "Swap"
                    )}
                </button>
            </div>

            <AnimatePresence>
                {showApproveModal && (
                    <div
                        className="fixed inset-0 bg-gray-950/60 flex items-center justify-center z-50 px-4"
                        onClick={handleCloseApprove}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full max-w-md rounded-2xl border-2 border-[#9770DD]/60 bg-gradient-to-br from-[#050505]/95 to-black/95 p-8 shadow-2xl"
                        >
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-semibold text-white">
                                    {t("common.approvalRequired") || "Approval Required"}
                                </h2>
                                {!isApproving && (
                                    <button
                                        onClick={handleCloseApprove}
                                        className="size-8 rounded-full text-white hover:bg-white/10 flex items-center justify-center"
                                    >
                                        <FiX />
                                    </button>
                                )}
                            </div>

                            {approvalStatus === null && (
                                <>
                                    <p className="text-white/80 mb-6 leading-relaxed">
                                        {t("swap.approvalMessage") || "Please approve token spending before swapping."}
                                    </p>
                                    <div className="flex gap-3 justify-end">
                                        <button
                                            onClick={handleCloseApprove}
                                            disabled={isApproving}
                                            className="px-6 py-3 rounded-lg border border-white/30 text-white hover:bg-white/10 disabled:opacity-50"
                                        >
                                            {t("common.cancel") || "Cancel"}
                                        </button>
                                        <button
                                            onClick={approveSwap}
                                            disabled={isApproving}
                                            className="px-6 py-3 rounded-lg text-white font-semibold bg-gradient-to-r from-[#9770DD] to-[#ff6b6b] hover:scale-105 transition-transform disabled:opacity-60"
                                        >
                                            {isApproving ? (t("common.approving") || "Approving...") : (t("common.approve") || "Approve")}
                                        </button>
                                    </div>
                                </>
                            )}

                            {approvalStatus === "pending" && (
                                <div className="text-center py-5">
                                    <div className="size-14 border-4 border-[#9770DD]/30 border-t-[#9770DD] rounded-full animate-spin mx-auto mb-4" />
                                    <p className="text-white text-lg font-semibold mb-2">
                                        {t("common.processingApproval") || "Processing Approval"}
                                    </p>
                                    <p className="text-white/70 text-sm">
                                        {t("common.confirmTransaction") || "Please confirm transaction in wallet"}
                                    </p>
                                </div>
                            )}

                            {approvalStatus === "success" && (
                                <div className="text-center py-5">
                                    <div className="size-14 rounded-full bg-green-500/20 text-green-400 text-3xl flex items-center justify-center mx-auto mb-4">✓</div>
                                    <p className="text-white text-lg font-semibold mb-2">
                                        {t("common.approvalSuccessful") || "Approval Successful"}
                                    </p>
                                    <p className="text-white/70 text-sm">
                                        {t("swap.canSwapNow") || "You can continue swapping now."}
                                    </p>
                                </div>
                            )}

                            {approvalStatus === "error" && (
                                <div className="text-center py-5">
                                    <div className="size-14 rounded-full bg-red-500/20 text-red-400 text-3xl flex items-center justify-center mx-auto mb-4">×</div>
                                    <p className="text-white text-lg font-semibold mb-2">
                                        {t("common.approvalFailed") || "Approval Failed"}
                                    </p>
                                    <p className="text-white/70 text-sm mb-5">
                                        {approvalError || t("common.tryAgain") || "Please try again"}
                                    </p>
                                    <div className="flex gap-3 justify-center">
                                        <button
                                            onClick={handleCloseApprove}
                                            className="px-6 py-3 rounded-lg border border-white/30 text-white hover:bg-white/10"
                                        >
                                            {t("common.close") || "Close"}
                                        </button>
                                        <button
                                            onClick={approveSwap}
                                            className="px-6 py-3 rounded-lg text-white font-semibold bg-gradient-to-r from-[#9770DD] to-[#ff6b6b]"
                                        >
                                            {t("common.tryAgain") || "Try Again"}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* <AnimatePresence>
                {showSwapModal && (
                    <div className="fixed inset-0 z-50 bg-gray-950/60 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => !isSwapping && setShowSwapModal(false)}>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full max-w-sm rounded-2xl bg-[#1a1023] border border-[#9770DD]/40 p-6 shadow-xl shadow-[#9770DD]/20"
                        >
                            <div className="flex items-center gap-3 mb-5">
                                <FiSettings className="size-5 text-[#DFD2FE]" />
                                <h3 className="text-white text-lg font-semibold">
                                    {t("swap.confirmSwap") || "Confirm Swap"}
                                </h3>
                                {!isSwapping && (
                                    <button
                                        onClick={() => setShowSwapModal(false)}
                                        className="ml-auto size-8 rounded-full text-white hover:bg-white/10 flex items-center justify-center -mr-2 transition-colors"
                                    >
                                        <FiX />
                                    </button>
                                )}
                            </div>
                            <p className="text-sm text-[#ad90cb] mb-6">
                                {t("swap.confirmDesc") || "Please review your swap details before confirming."}
                            </p>

                            <div className="rounded-xl border border-[#9770DD]/30 bg-[#241630]/60 px-5 py-4 mb-3 flex items-center justify-between shadow-inner">
                                <div className="text-sm font-medium text-[#ad90cb]">{t("swap.from") || "From"}</div>
                                <div className="text-white font-bold text-xl flex items-center gap-2">
                                    {fromAmount} <span className="text-[#9770DD] font-bold text-sm">aBnBUsdt</span>
                                </div>
                            </div>

                            <div className="flex justify-center -my-3.5 relative z-10 pointer-events-none">
                                <div className="size-8 rounded-full bg-[#1a1023] border-4 border-[#1a1023] flex items-center justify-center">
                                    <div className="w-full h-full rounded-full bg-[#241630] border border-[#9770DD]/40 flex items-center justify-center text-[#DFD2FE] shadow-sm">
                                        <FiArrowDown className="size-3.5" />
                                    </div>
                                </div>
                            </div>

                            <div className="rounded-xl border border-[#9770DD]/30 bg-[#241630]/60 px-5 py-4 mb-6 flex items-center justify-between shadow-inner">
                                <div className="text-sm font-medium text-[#ad90cb]">{t("swap.to") || "To"}</div>
                                <div className="text-white font-bold text-xl flex items-center gap-2">
                                    {toAmount} <span className="text-[#9770DD] font-bold text-sm">USDT</span>
                                </div>
                            </div>

                            <div className="flex justify-between items-center px-3 mb-6 text-sm bg-[#9770DD]/10 rounded-lg py-2 border border-[#9770DD]/20">
                                <span className="text-[#ad90cb] font-medium flex items-center gap-1.5"><FiInfo className="size-4" /> {t("swap.exchangeRate") || "Exchange Rate"}</span>
                                <span className="font-bold text-[#DFD2FE]">1 aBnBUsdt = {exchangeRate} USDT</span>
                            </div>

                            <div className="flex gap-3 justify-end mt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowSwapModal(false)}
                                    disabled={isSwapping}
                                    className="px-6 py-3.5 rounded-xl border border-white/20 text-white font-semibold hover:bg-white/10 hover:border-white/40 disabled:opacity-50 flex-1 transition-all"
                                >
                                    {t("common.cancel") || "Cancel"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowSwapModal(false);
                                        handleSwap();
                                    }}
                                    disabled={isSwapping || !account}
                                    className="px-6 py-3.5 rounded-xl text-white font-bold bg-gradient-to-r from-[#9770DD] to-[#ff6b6b] hover:from-[#7a1fd6] hover:to-[#e05656] hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-[#9770DD]/30 transition-all disabled:opacity-60 disabled:scale-100 flex-1 flex items-center justify-center gap-2"
                                >
                                    {isSwapping ? (
                                        <div className="size-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        t("swap.confirmSwap") || "Confirm Swap"
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence> */}

            <AnimatePresence>
                {showNewSwapModal && (
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 50 }}
                        className="fixed inset-0 z-[80] bg-[#05010D] flex flex-col pt-4 px-4 pb-[120px] overflow-y-auto"
                    >
                        <div className="max-w-[550px] w-full mx-auto flex-1 flex flex-col relative">
                            {/* Header */}
                            <div className="flex items-center mb-10 pt-3">
                                <button onClick={() => setShowNewSwapModal(false)} className="material-symbols-outlined text-white text-2xl">arrow_back</button>
                            </div>

                            {/* Token Icons Animation */}
                            <div className="flex justify-center items-center mb-8 relative">
                                {/* Left Token */}
                                {swapPair === "ABNB_TO_USDT" ? (
                                    <div className="size-[84px] rounded-full bg-[#E5D9F2] flex items-center justify-center text-3xl font-bold text-[#5B47CD] shadow-lg relative z-10 -mr-4 border-4 border-[#05010D]">
                                        aB
                                    </div>
                                ) : (
                                    <div className="size-[84px] rounded-full bg-[#22C55E] flex items-center justify-center shadow-lg relative z-10 -mr-4 border-4 border-[#05010D] overflow-hidden">
                                        <img src={tokenIconUsdt} alt="USDT" className="size-[85%] object-cover rounded-full" />
                                    </div>
                                )}

                                {/* Center Arrow */}
                                <div className="size-6 rounded-full bg-white flex items-center justify-center relative z-30 shadow-md">
                                    <span className="material-symbols-outlined text-black text-[16px] font-bold">arrow_forward</span>
                                </div>

                                {/* Right Token */}
                                {swapPair === "ABNB_TO_USDT" ? (
                                    <div className="size-[84px] rounded-full bg-[#22C55E] flex items-center justify-center shadow-lg relative z-20 -ml-4 border-4 border-[#05010D] overflow-hidden">
                                        <img src={tokenIconUsdt} alt="USDT" className="size-[85%] object-cover rounded-full" />
                                    </div>
                                ) : (
                                    <div className="size-[84px] rounded-full bg-[#FEF3C7] flex items-center justify-center text-3xl font-bold text-[#D97706] shadow-lg relative z-20 -ml-4 border-4 border-[#05010D]">
                                        GR
                                    </div>
                                )}
                            </div>

                            {/* Buy Amount */}
                            <div className="text-center mb-7">
                                <p className="text-white text-base mb-2">{t('swap.buy')}</p>
                                <h2 className="text-[#A855F7] text-[40px] font-semibold leading-none mb-4">
                                    {toAmount || "0.00"} {swapPair === "ABNB_TO_USDT" ? "USDT" : "GRAM"}
                                </h2>
                                <p className="text-white text-sm">{t('swap.at')} {swapPair === "ABNB_TO_USDT" ? "aBnBUsdt" : "USDT"} {fromAmount || "0.00"}</p>
                            </div>

                            {/* Details Card */}
                            <div className="w-full bg-transparent rounded-3xl border border-[#2A1A42] p-5 mb-8 flex flex-col gap-5">
                                <div className="flex justify-between items-center">
                                    <span className="text-[#938F99] text-[15px]">{t('swap.spend')}</span>
                                    <span className="text-white font-bold text-[15px]">{fromAmount || "0.00"} {swapPair === "ABNB_TO_USDT" ? "aBnBUsdt" : "USDT"}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-[#938F99] text-[15px]">{t('swap.receive')}</span>
                                    <span className="text-white font-bold text-[15px]">{toAmount || "0.00"} {swapPair === "ABNB_TO_USDT" ? "USDT" : "GRAM"}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-[#938F99] text-[15px]">{t('swap.newBalance', { token: swapPair === "ABNB_TO_USDT" ? "USDT" : "GRAM" })}</span>
                                    <span className="text-white font-bold text-[15px]">{receivedBalance} {swapPair === "ABNB_TO_USDT" ? "USDT" : "GRAM"}</span>
                                </div>

                                {/* Warning */}
                                <div className="flex items-center gap-2 mt-2">
                                    <span className="material-symbols-outlined text-[#EF4444] text-[18px]">error</span>
                                    <span className="text-[#EF4444] text-sm font-medium">{t('swap.confirmDesc')}</span>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="mt-auto pb-2 sticky bottom-0 bg-[#05010D]/95 pt-3">
                                <button
                                    onClick={handleSwap}
                                    disabled={isSwapping}
                                    className="w-full h-[54px] rounded-full bg-[#5B47CD] text-white font-bold text-lg hover:bg-[#4C3BBA] active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {isSwapping ? (
                                        <>
                                            <div className="size-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            {t('swap.swapping') || "Swapping..."}
                                        </>
                                    ) : (
                                        t('swap.confirmSwap')
                                    )}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div >
    );
};
